import {
 GridColDef,
 GridFilterModel,
 GridInitialState,
 GridPaginationModel,
 GridRowParams,
 GridSortModel,
 MuiEvent,
} from '@mui/x-data-grid-premium';
import {
 MutableRefObject,
 useCallback,
 useEffect,
 useMemo,
 useRef,
 useState,
} from 'react';

import {
 getConfigStorageItem,
 setConfigStorageItem,
 storagePrefix,
} from '../../../../hooks/useConfigStorage';
import {
 changeCurrentPreset,
 ColPresetType,
 getClientConfigurationSelfStart,
 PageNameType,
} from '../../../../redux/clientConfiguration/clientConfigurationSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { getBaseDomain } from '../../../../redux/pageDomain';
import { RootState } from '../../../../redux/store';
import { AUTOSIZE_DELAY_MS } from '../constants';
import { CustomGridApi } from '../types';
import { useXTableContext } from '../XTableContext';

interface UseTableInitParams {
 pageName: PageNameType;
 apiRef: MutableRefObject<CustomGridApi>;
 table: string;
 defaultPageSize: number;
 columnsState: GridColDef[];
 presetsColumns: { [key: string]: ColPresetType[] } | null;
}

const useTableInit = ({
 pageName,
 apiRef,
 table,
 defaultPageSize,
 columnsState,
 presetsColumns,
}: UseTableInitParams) => {
 const { currentId, setCurrentId } = useXTableContext();
 const dispatch = useAppDispatch();

 const success = useAppSelector((state: RootState) => {
  const pageState = state[getBaseDomain(pageName)];
  return pageState && 'success' in pageState ? pageState.success : false;
 });

 const loading = useAppSelector((state: RootState) => {
  const pageState = state[getBaseDomain(pageName)];
  return pageState && 'loading' in pageState
   ? !!(pageState.loading?.getTable || pageState.loading?.getTableStructure)
   : false;
 });

 const [isInit, setIsInit] = useState(false);
 const [filter, setFilter] = useState<GridFilterModel>();
 const [sortModel, setSortModel] = useState<GridSortModel>([]);
 const defaultPaginationModel = useMemo(
  () => ({ page: 0, pageSize: defaultPageSize }),
  [defaultPageSize],
 );
 const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
  defaultPaginationModel,
 );
 const [initialState, setInitialState] = useState<GridInitialState>({
  pinnedColumns: { right: ['actions'] },
 });

 const autosizeTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
  undefined,
 );
 const isSelectingRef = useRef(false);

 useEffect(() => {
  return () => {
   if (autosizeTimerRef.current !== undefined) {
    clearTimeout(autosizeTimerRef.current);
   }
  };
 }, []);

 useEffect(() => {
  const tableSuccess = (success as Record<string, boolean>)?.getTable;
  if (!tableSuccess) return;

  if (autosizeTimerRef.current !== undefined) {
   clearTimeout(autosizeTimerRef.current);
  }
  autosizeTimerRef.current = setTimeout(() => {
   autosizeTimerRef.current = undefined;
   apiRef.current?.autosizeColumns?.({
    columns: columnsState
     .filter((col) => col.width === undefined)
     .map((col) => col.field),
    includeHeaders: false,
    includeOutliers: true,
   });
  }, AUTOSIZE_DELAY_MS);
 }, [(success as Record<string, boolean>)?.getTable, columnsState]);

 const saveSnapshot = useCallback(() => {
  if (apiRef?.current?.exportState && localStorage) {
   const currentState = apiRef.current.exportState();
   localStorage.setItem(
    `${storagePrefix}_table_state_${pageName}`,
    JSON.stringify(currentState),
   );
  }
 }, [apiRef, pageName]);

 useEffect(() => {
  const handleMouseDown = () => {
   isSelectingRef.current = false;
  };

  const handleMouseMove = () => {
   isSelectingRef.current = true;
  };

  const stateFromLocalStorage = JSON.parse(
   localStorage?.getItem(`${storagePrefix}_table_state_${pageName}`) || '{}',
  );
  setSortModel(stateFromLocalStorage?.sorting?.sortModel || []);
  setFilter(stateFromLocalStorage?.filter?.filterModel || { items: [] });
  setPaginationModel(
   stateFromLocalStorage?.pagination?.paginationModel || defaultPaginationModel,
  );
  setInitialState(stateFromLocalStorage);

  const lsPreset = getConfigStorageItem(table)?.columns;
  if (lsPreset) {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: lsPreset,
     temp: false,
    }),
   );
  } else if (presetsColumns?.[pageName]?.[0]) {
   dispatch(
    changeCurrentPreset({
     page: pageName,
     type: 'columns',
     preset: presetsColumns[pageName][0],
     temp: false,
    }),
   );
   setConfigStorageItem({
    page: pageName as PageNameType,
    key: 'columns',
    value: presetsColumns[pageName][0],
   });
  }

  if (!presetsColumns) {
  dispatch(getClientConfigurationSelfStart({ cached: true }));
  }

  setIsInit(true);

  const urlId = new URLSearchParams(window.location.search).get('id');
  if (urlId && urlId !== 'undefined') {
   const normalizedId = Number.isNaN(Number(urlId))
    ? urlId
    : Number(urlId);
   setCurrentId(normalizedId);
  }

  const unsubscribe = apiRef.current.subscribeEvent(
   'rowClick',
   (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
    event.defaultMuiPrevented = true;
    if (isSelectingRef.current) {
     return true;
    }

    if (params.row?.Id && !isSelectingRef.current) {
     setCurrentId(params.row.Id);
    }
    return true;
   },
  );

  document.addEventListener('mousedown', handleMouseDown);
  document.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('beforeunload', saveSnapshot);
  return () => {
   unsubscribe();
   document.removeEventListener('mousedown', handleMouseDown);
   document.removeEventListener('mousemove', handleMouseMove);
   window.removeEventListener('beforeunload', saveSnapshot);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);

  if (currentId) {
   window.history.pushState(
    {},
    '',
    `${window.location.pathname}?id=${currentId}`,
   );
  } else if (isInit && urlParams.has('id')) {
   urlParams.delete('id');
   const newUrl = urlParams.toString()
    ? `${window.location.pathname}?${urlParams.toString()}`
    : window.location.pathname;
   window.history.pushState({}, '', newUrl);
  }
 }, [currentId, isInit]);

 return {
  isInit,
  filter,
  setFilter,
  sortModel,
  setSortModel,
  paginationModel,
  setPaginationModel,
  initialState,
  loading,
 };
};

export default useTableInit;

